/**
 * A sessão do pai: o Sapron embutido no portal do franqueado, entrando com o
 * token do IDP que o portal entregou pelo handshake (`SsoEntry/handshake.ts`).
 *
 * Não é sessão legada. O token do IDP dura 300 s, quem o renova é o portal, e o
 * contrato do IDP proíbe token fora da memória
 * (`seazone-idp/docs/integracao/proibicoes.md`, item 1): o token mora só aqui,
 * nunca em `localStorage` nem em `sessionStorage`, e nada daqui chama o refresh
 * legado ou o IDP — dentro da moldura o IDP nem carrega.
 *
 * Quatro peças mantêm a sessão viva enquanto o app está em moldura:
 * - o pai reenvia `token` a cada renovação, e um listener da vida toda do app
 *   (não da página `SsoEntry`, que sai com o `window.location.replace`) troca o
 *   token em uso, só para a mesma conta;
 * - perto de vencer o filho re-anuncia `ready`, e o pai responde com um token
 *   fresco — cobre o franqueado que fica no Programa de Indicação sem o portal
 *   chamar a API (o portal só renova quando ele mesmo faz requisição). "Perto
 *   de vencer" é contado pelo `expiresIn` que o pai manda, no relógio desta
 *   máquina, nunca pelo `exp` do JWT (relógio do IDP);
 * - recarregar o documento apaga a memória: a marca da moldura (um "1", sem
 *   token) faz o boot pedir o token ao pai de novo (`retomarSessaoDoPai`);
 * - a API recusar o token (401) pede um token novo ao pai e repete uma vez
 *   (`pedirTokenAoPai`); sem token novo, a sessão acaba e a moldura volta à
 *   entrada (`desistirDaSessaoDoPai`) — nunca ao `/login#expired`, que
 *   produção serve com `X-Frame-Options: SAMEORIGIN`.
 */
import jwt_decode from 'jwt-decode';

import {
  readTokenMessage,
  readyMessage,
  type TokenMessage,
} from '@/pages/Partners/pages/SsoEntry/handshake';

import { dentroDeMoldura } from './moldura';
import { localStorageSeguro, sessionStorageSeguro } from './storage';

/** Marca, por aba, de que a sessão desta moldura vem do pai. Nunca o token. */
export const MARCA_DA_SESSAO_DO_PAI = 'sapron-sessao-do-pai';

/**
 * A última recusa da sessão do pai pela API: quando foi e a impressão (nunca o
 * token) do token recusado. Dentro da janela a entrada não abre outra sessão
 * do pai com **esse** token: sem a guarda, o pai devolveria o token recusado e
 * a moldura ficaria em laço entrada → painel → 401 → entrada. Outro token do
 * pai (renovou depois do limite do pedido) abre normalmente. Mesmo desenho da
 * guarda de laço de `idp/recusa-da-api.ts`, presa ao token.
 */
export const RECUSA_DA_API_KEY = 'sapron-sessao-do-pai-recusada-em';
const JANELA_DA_RECUSA_MS = 2 * 60 * 1000;

interface Recusa {
  readonly em: number;
  readonly impressao?: string;
}

/**
 * Para onde a moldura vai quando a sessão do pai acaba: a entrada é o único
 * documento que a borda deixa embutir; ela anuncia `ready` de novo e, sem
 * sessão possível, oferece o login da moldura.
 */
export const ENTRADA_DA_MOLDURA = '/parceiros/entrada?target=partner';

const SESSAO_LEGADA_KEY = 'sapron-pms-user';

/** O filho pede token novo quando falta isto para vencer: a folga com que o portal renova o dele. */
const ANTECEDENCIA_DO_PEDIDO_S = 30;

/**
 * O pai respondeu com o token que já estava em uso (ainda não era hora de
 * renovar, pelo relógio dele): o próximo pedido espera isto, nunca sai na hora.
 */
const RECUO_DO_TOKEN_REPETIDO_MS = 5000;

/** Mesmo ritmo e mesmo limite da espera da `SsoEntry`. */
const REANUNCIO_MS = 500;
const LIMITE_DA_RETOMADA_MS = 15000;

/** Depois de um 401: quanto o filho espera o pai trocar o token antes de desistir da sessão. */
const LIMITE_DO_PEDIDO_MS = 5000;

const MS_POR_SEGUNDO = 1000;

interface Conta {
  readonly sapronUserId?: string;
  readonly email?: string;
}

interface Sessao {
  readonly token: string;
  readonly conta: Conta;
  /** Quando o token vence, em ms do relógio desta máquina. */
  readonly venceEm: number;
}

interface Pedido {
  readonly promessa: Promise<string | undefined>;
  readonly terminar: (token?: string) => void;
}

let atual: Sessao | undefined;
let pedidoAgendado = 0;
let pedidoApos401: Pedido | undefined;
let escutando = false;

function claimsDe(token: string): Record<string, unknown> | undefined {
  try {
    return jwt_decode<Record<string, unknown>>(token);
  } catch {
    return undefined;
  }
}

/**
 * Impressão não secreta de um token: o `jti`, que o Keycloak sempre põe; sem
 * ele, `iat:exp`. Serve para reconhecer o token recusado sem guardá-lo.
 */
function impressaoDe(token: string): string | undefined {
  const claims = claimsDe(token);
  if (typeof claims?.jti === 'string' && claims.jti !== '') return claims.jti;
  if (typeof claims?.exp === 'number') return `${claims.iat ?? ''}:${claims.exp}`;
  return undefined;
}

function lerRecusa(): Recusa | undefined {
  try {
    const gravada = sessionStorageSeguro()?.getItem(RECUSA_DA_API_KEY);
    if (!gravada) return undefined;
    const recusa = JSON.parse(gravada) as Partial<Recusa>;
    return typeof recusa.em === 'number'
      ? { em: recusa.em, impressao: recusa.impressao }
      : undefined;
  } catch {
    return undefined;
  }
}

function sessaoDe(entrega: TokenMessage): Sessao | undefined {
  const claims = claimsDe(entrega.token);
  if (typeof claims?.iss !== 'string' || typeof claims.exp !== 'number') return undefined;
  const vinculo = claims.sapron_user_id;
  // Com `expiresIn` o vencimento é contado daqui, no relógio desta máquina;
  // sem ele (pai de antes do campo) vale o `exp` do JWT, como der.
  const venceEm =
    entrega.expiresIn !== undefined
      ? Date.now() + entrega.expiresIn * MS_POR_SEGUNDO
      : claims.exp * MS_POR_SEGUNDO;
  return {
    token: entrega.token,
    venceEm,
    conta: {
      ...((typeof vinculo === 'number' || typeof vinculo === 'string') && {
        sapronUserId: String(vinculo),
      }),
      ...(typeof claims.email === 'string' && { email: claims.email.toLowerCase() }),
    },
  };
}

/** Por que um token novo não substitui o atual; `undefined` quando é da mesma conta. */
function motivoParaIgnorar(a: Conta, b: Conta): 'outra_conta' | 'sem_identidade' | undefined {
  if (a.sapronUserId !== undefined && b.sapronUserId !== undefined) {
    return a.sapronUserId === b.sapronUserId ? undefined : 'outra_conta';
  }
  if (a.email !== undefined && b.email !== undefined) {
    return a.email === b.email ? undefined : 'outra_conta';
  }
  return 'sem_identidade';
}

function anunciar() {
  // O `ready` não carrega dado nenhum; a origem do pai só é conhecida quando ele
  // responde, e a resposta é validada em `readTokenMessage` (mesmo caso da `SsoEntry`).
  // nosemgrep: sast-rules.pr-blocking.javascript.browser.security.wildcard-postmessage-configuration
  window.parent.postMessage(readyMessage(), '*');
}

function faltaPouco(sessao: Sessao): boolean {
  return sessao.venceEm - ANTECEDENCIA_DO_PEDIDO_S * MS_POR_SEGUNDO <= Date.now();
}

function agendarPedido(esperaMinimaMs = 0) {
  window.clearTimeout(pedidoAgendado);
  if (!atual) return;
  const espera = atual.venceEm - ANTECEDENCIA_DO_PEDIDO_S * MS_POR_SEGUNDO - Date.now();
  pedidoAgendado = window.setTimeout(anunciar, Math.max(espera, esperaMinimaMs));
}

/** Aba em segundo plano atrasa timer: na volta, quem já passou da hora pede na hora. */
function aoVoltarParaAAba() {
  if (document.visibilityState === 'visible' && atual && faltaPouco(atual)) anunciar();
}

function aoReceber(event: MessageEvent) {
  if (!atual) return;
  const entrega = readTokenMessage(event, window.parent);
  if (!entrega) return;

  const nova = sessaoDe(entrega);
  if (!nova) return;
  const motivo = motivoParaIgnorar(atual.conta, nova.conta);
  if (motivo) {
    // Token de outra conta no meio da sessão é troca de identidade: fica a sessão
    // aberta. O log leva só o evento, nunca o token.
    // eslint-disable-next-line no-console
    console.warn(JSON.stringify({ event: 'sessao_do_pai.token_ignorado', motivo }));
    return;
  }
  const repetido = nova.token === atual.token;
  atual = nova;
  agendarPedido(repetido ? RECUO_DO_TOKEN_REPETIDO_MS : 0);
  if (!repetido) pedidoApos401?.terminar(nova.token);
}

function escutarOPai() {
  if (escutando) return;
  escutando = true;
  window.addEventListener('message', aoReceber);
  document.addEventListener('visibilitychange', aoVoltarParaAAba);
}

export function encerrarSessaoDoPai(): void {
  atual = undefined;
  window.clearTimeout(pedidoAgendado);
  pedidoApos401?.terminar();
  sessionStorageSeguro()?.removeItem(MARCA_DA_SESSAO_DO_PAI);
}

/**
 * Abre a sessão do pai com o token do IDP que ele entregou. Devolve o e-mail da
 * conta, ou `undefined` quando o token não é do IDP (o legado segue o caminho
 * de sempre, e nenhuma marca de sessão do pai fica para trás).
 */
export function abrirSessaoDoPai(entrega: TokenMessage): { email: string } | undefined {
  const sessao = sessaoDe(entrega);
  if (!sessao) {
    encerrarSessaoDoPai();
    return undefined;
  }

  atual = sessao;
  try {
    sessionStorageSeguro()?.setItem(MARCA_DA_SESSAO_DO_PAI, '1');
  } catch {
    // Sem storage não há marca: recarregar a moldura volta pela `SsoEntry`.
  }
  // Sobra de sessão legada na moldura: o guard de rota e o link do GeoSeazone
  // leem `sapron-pms-user` direto — uma sobra vencida derrubaria a moldura, e
  // uma válida assinaria o link com outra conta.
  localStorageSeguro()?.removeItem(SESSAO_LEGADA_KEY);
  escutarOPai();
  agendarPedido();
  return { email: sessao.conta.email ?? '' };
}

/** O token que o interceptor deve mandar, ou `undefined` sem sessão do pai. */
export function tokenDaSessaoDoPai(): string | undefined {
  return atual?.token;
}

/** A moldura teve sessão do pai antes de recarregar: o boot pede o token de novo. */
export function temMarcaDaSessaoDoPai(): boolean {
  return dentroDeMoldura() && sessionStorageSeguro()?.getItem(MARCA_DA_SESSAO_DO_PAI) === '1';
}

/**
 * A API recusou uma requisição com o token do pai. Pede um token novo (`ready`)
 * e resolve com ele; com o token em uso já trocado (outro 401 ou a renovação
 * chegou antes), resolve na hora; sem token novo até o limite, `undefined`.
 * Vários 401 juntos dividem um pedido só.
 */
export function pedirTokenAoPai(tokenRecusado: string): Promise<string | undefined> {
  if (!atual) return Promise.resolve(undefined);
  if (atual.token !== tokenRecusado) return Promise.resolve(atual.token);
  if (pedidoApos401) return pedidoApos401.promessa;

  let intervalo = 0;
  let limite = 0;
  let resolver: (token?: string) => void = () => {};
  const promessa = new Promise<string | undefined>((resolve) => {
    resolver = resolve;
  });
  const terminar = (token?: string) => {
    window.clearInterval(intervalo);
    window.clearTimeout(limite);
    pedidoApos401 = undefined;
    resolver(token);
  };
  pedidoApos401 = { promessa, terminar };
  intervalo = window.setInterval(anunciar, REANUNCIO_MS);
  limite = window.setTimeout(() => terminar(), LIMITE_DO_PEDIDO_MS);
  anunciar();
  return promessa;
}

/**
 * A sessão do pai acabou: a API recusou o token e o pai não trocou por outro.
 * Encerra tudo, marca a recusa (a entrada não reabre sessão do pai com esse
 * token por um tempo) e leva a moldura à entrada, o documento que a borda
 * deixa embutir.
 */
export function desistirDaSessaoDoPai(): void {
  const recusa: Recusa = { em: Date.now(), impressao: atual && impressaoDe(atual.token) };
  encerrarSessaoDoPai();
  try {
    sessionStorageSeguro()?.setItem(RECUSA_DA_API_KEY, JSON.stringify(recusa));
  } catch {
    // Sem storage não há guarda; a entrada tenta de novo, como sem marca.
  }
  window.location.replace(ENTRADA_DA_MOLDURA);
}

/** O token é do IDP e é o mesmo que a API recusou há pouco: a entrada não abre outra sessão com ele. */
export function sessaoDoPaiRecusadaHaPouco(entrega: TokenMessage): boolean {
  if (!sessaoDe(entrega)) return false;
  const recusa = lerRecusa();
  if (!recusa || Date.now() - recusa.em >= JANELA_DA_RECUSA_MS) return false;
  return recusa.impressao !== undefined && recusa.impressao === impressaoDe(entrega.token);
}

/**
 * Pede o token ao pai no boot de um documento recarregado na moldura: anuncia
 * `ready` (e repete, o pai pode estar ocupado) até o token chegar ou o limite.
 */
export function retomarSessaoDoPai(): Promise<TokenMessage | undefined> {
  return new Promise((resolve) => {
    const controle = new AbortController();
    let intervalo = 0;
    let limite = 0;

    const terminar = (entrega?: TokenMessage) => {
      controle.abort();
      window.clearInterval(intervalo);
      window.clearTimeout(limite);
      resolve(entrega);
    };

    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        const entrega = readTokenMessage(event, window.parent);
        if (entrega) terminar(entrega);
      },
      { signal: controle.signal },
    );
    intervalo = window.setInterval(anunciar, REANUNCIO_MS);
    limite = window.setTimeout(() => terminar(), LIMITE_DA_RETOMADA_MS);
    anunciar();
  });
}
